import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ResourceCrudPage, Stack, TabView, type ResourceListArgs, type PaginatedResult } from '@vipso/shared';
import {
  useListChambersQuery,
  useAddUpdateChamberMutation,
  useDeleteChamberMutation,
  useListFloorsQuery,
  useAddUpdateFloorMutation,
  useDeleteFloorMutation,
  useListRacksQuery,
  useAddUpdateRackMutation,
  useDeleteRackMutation,
} from '../api/tenantApi';
import { NoAccessState } from '../components/RequireFeature';
import { useModuleAccess } from '../hooks/useModuleAccess';
import { useNavAccess } from '../hooks/useNavAccess';

type NamedRow = { id: string; name: string; chamberId?: string; floorId?: string };

function useChamberList(args: ResourceListArgs) {
  const result = useListChambersQuery(args);
  const data: PaginatedResult<NamedRow> | undefined = result.data?.listChambers
    ? {
        ...result.data.listChambers,
        rows: result.data.listChambers.rows.map((r) => ({ id: r.id, name: r.name })),
      }
    : undefined;
  return { ...result, data };
}

function ChambersTab() {
  const { t } = useTranslation();
  const access = useModuleAccess('chamber');
  const [save, { isLoading: saving }] = useAddUpdateChamberMutation();
  const [remove] = useDeleteChamberMutation();

  return (
    <ResourceCrudPage<NamedRow>
      title={t('storage.chambers')}
      description={t('storage.chambersDescription')}
      entityLabel={t('storage.chamberEntity')}
      canCreate={access.canCreate}
      canUpdate={access.canUpdate}
      canDelete={access.canDelete}
      saving={saving}
      refreshLabel={t('common.refresh')}
      useList={useChamberList}
      fields={[{ key: 'name', label: t('common.name'), required: true }]}
      onSave={(values, editing) =>
        save({
          name: values.name.trim(),
          ...(editing?.id ? { id: editing.id } : {}),
        }).unwrap()
      }
      onDelete={(row) => remove({ id: row.id }).unwrap()}
    />
  );
}

function FloorsTab() {
  const { t } = useTranslation();
  const access = useModuleAccess('floor');
  const [save, { isLoading: saving }] = useAddUpdateFloorMutation();
  const [remove] = useDeleteFloorMutation();
  const { data: chambers } = useListChambersQuery({ page: 1, pageLimit: 200 });
  const chamberOptions = useMemo(
    () => (chambers?.listChambers.rows ?? []).map((c) => ({ value: c.id, label: c.name })),
    [chambers],
  );

  function useFloorList(args: ResourceListArgs) {
    const result = useListFloorsQuery(args);
    const data: PaginatedResult<NamedRow> | undefined = result.data?.listFloors
      ? {
          ...result.data.listFloors,
          rows: result.data.listFloors.rows.map((r) => ({
            id: r.id,
            name: r.name,
            chamberId: r.chamberId,
          })),
        }
      : undefined;
    return { ...result, data };
  }

  return (
    <ResourceCrudPage<NamedRow>
      title={t('storage.floors')}
      description={t('storage.floorsDescription')}
      entityLabel={t('storage.floorEntity')}
      canCreate={access.canCreate}
      canUpdate={access.canUpdate}
      canDelete={access.canDelete}
      saving={saving}
      refreshLabel={t('common.refresh')}
      useList={useFloorList}
      fields={[
        { key: 'name', label: t('common.name'), required: true },
        {
          key: 'chamberId',
          label: t('orders.chamber'),
          required: true,
          options: chamberOptions,
        },
      ]}
      onSave={(values, editing) =>
        save({
          name: values.name.trim(),
          chamberId: values.chamberId,
          ...(editing?.id ? { id: editing.id } : {}),
        }).unwrap()
      }
      onDelete={(row) => remove({ id: row.id }).unwrap()}
      subtitleOf={(row) => {
        const c = chamberOptions.find((o) => o.value === row.chamberId);
        return c ? t('storage.chamberOf', { name: c.label }) : undefined;
      }}
    />
  );
}

function RacksTab() {
  const { t } = useTranslation();
  const access = useModuleAccess('rack');
  const [save, { isLoading: saving }] = useAddUpdateRackMutation();
  const [remove] = useDeleteRackMutation();
  const { data: floors } = useListFloorsQuery({ page: 1, pageLimit: 200 });
  const floorOptions = useMemo(
    () => (floors?.listFloors.rows ?? []).map((f) => ({ value: f.id, label: f.name })),
    [floors],
  );

  function useRackList(args: ResourceListArgs) {
    const result = useListRacksQuery(args);
    const data: PaginatedResult<NamedRow> | undefined = result.data?.listRacks
      ? {
          ...result.data.listRacks,
          rows: result.data.listRacks.rows.map((r) => ({
            id: r.id,
            name: r.name,
            floorId: r.floorId,
          })),
        }
      : undefined;
    return { ...result, data };
  }

  return (
    <ResourceCrudPage<NamedRow>
      title={t('storage.racks')}
      description={t('storage.racksDescription')}
      entityLabel={t('storage.rackEntity')}
      canCreate={access.canCreate}
      canUpdate={access.canUpdate}
      canDelete={access.canDelete}
      saving={saving}
      refreshLabel={t('common.refresh')}
      useList={useRackList}
      fields={[
        { key: 'name', label: t('common.name'), required: true },
        {
          key: 'floorId',
          label: t('orders.floor'),
          required: true,
          options: floorOptions,
        },
      ]}
      onSave={(values, editing) =>
        save({
          name: values.name.trim(),
          floorId: values.floorId,
          ...(editing?.id ? { id: editing.id } : {}),
        }).unwrap()
      }
      onDelete={(row) => remove({ id: row.id }).unwrap()}
      subtitleOf={(row) => {
        const f = floorOptions.find((o) => o.value === row.floorId);
        return f ? t('storage.floorOf', { name: f.label }) : undefined;
      }}
    />
  );
}

export function StorageLocationsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const nav = useNavAccess();

  const allowedTabs = useMemo(() => {
    const tabs: Array<{ value: string; title: string; content: React.ReactNode }> = [];
    if (nav.chambers) {
      tabs.push({
        value: 'chambers',
        title: t('storage.chambers'),
        content: <ChambersTab />,
      });
    }
    if (nav.floors) {
      tabs.push({
        value: 'floors',
        title: t('storage.floors'),
        content: <FloorsTab />,
      });
    }
    if (nav.racks) {
      tabs.push({
        value: 'racks',
        title: t('storage.racks'),
        content: <RacksTab />,
      });
    }
    return tabs;
  }, [nav.chambers, nav.floors, nav.racks, t]);

  const [tab, setTab] = useState(() => allowedTabs[0]?.value ?? 'chambers');

  useEffect(() => {
    if (!allowedTabs.length) return;
    if (!allowedTabs.some((item) => item.value === tab)) {
      setTab(allowedTabs[0].value);
    }
  }, [allowedTabs, tab]);

  if (!allowedTabs.length) {
    return <NoAccessState onBack={() => navigate('/dashboard')} />;
  }

  return (
    <Stack gap="md">
      <TabView value={tab} onChange={setTab} tabs={allowedTabs} />
    </Stack>
  );
}
